import React, { useEffect, useMemo, useState } from "react";

import { mdiMovieOpenCogOutline, mdiTrashCanOutline } from "@mdi/js";
import Icon from "@mdi/react";
import moment from "moment";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import SelectedMediaOwnerInfo from "./SelectedMediaOwnerInfo";
import SelectedMediaSidePanelContent from "./SelectedMediaSidePanelContent";
import {
  API_DATE_FORMAT,
  TIME_FORMAT_LONG,
} from "../../../constants/dateFormat";
import useFAST from "../../../contexts/FASTContext";
import useDeleteScheduleListMediaMutation from "../../../hooks/mutations/useDeleteScheduleListMediaMutation";
import useUpdateScheduleListMediaMutation from "../../../hooks/mutations/useUpdateScheduleListMediaMutation";
import useFASTScheduleWebSocket from "../../../hooks/useFASTScheduleWebSocket";
import useTimelineItems from "../../../hooks/useTimelineItems";
import prettyDurations, { DURATION_UNITS } from "../../../utils/durations";
import { BUTTON_VARIANTS } from "../../button/Button";
import ConfirmModal from "../../modal/ConfirmModal";
import SidePanel from "../../sidePanel/SidePanel";

import type { JSX } from "react";

export interface SelectedMediaSidePanelFormValues {
  label?: string;
  duration?: string | number;
  startTime?: string;
  endTime?: string;
}

interface SelectedMediaSidePanelProps {
  isEditMode: boolean;
  isUpdateDisabled: boolean;
}

function SelectedMediaSidePanel({
  isEditMode,
  isUpdateDisabled,
}: SelectedMediaSidePanelProps): JSX.Element | null {
  const { dispatch, state } = useFAST();
  const { t } = useTranslation("editor-fast-channel");
  const updateMediaMutation = useUpdateScheduleListMediaMutation();
  const [showModal, setShowModal] = useState(false);

  const { timelineItemSelected } = state;

  const { getCanUpdateSchedule } = useFASTScheduleWebSocket();
  const { currentTimeslots, timelineContent } = useTimelineItems();

  const {
    label,
    uuid,
    userId,
    scheduleId,
    mediaId,
    duration,
    startTime,
    endTime,
    format,
    hasMediaVariant,
    nextMediaId,
    isOnBaseline,
    interstitialAdbreaks,
    updatedAt,
    poster,
  } = useMemo(() => {
    if (!timelineItemSelected || !timelineContent[timelineItemSelected])
      return {};

    const media = timelineContent[timelineItemSelected];

    return {
      label: media.label,
      uuid: media.uuid,
      userId: media.userId,
      mediaId: media.mediaId,
      duration: media.duration,
      startTime: media.startTime,
      endTime: media.endTime,
      format: media.format,
      scheduleId: media.scheduleId,
      hasMediaVariant: media.variantMediaList.length > 0,
      nextMediaId: media.nextMediaId,
      isOnBaseline: media.isOnBaseline,
      updatedAt: media.updatedAt,
      interstitialAdbreaks: media.interstitialAdbreaks,
      poster: media.poster,
    };
  }, [timelineContent, timelineItemSelected]);

  const fastDuration =
    duration &&
    prettyDurations(duration, { valueUnit: DURATION_UNITS.MILLISECONDS })
      .fastDuration;
  const prettyStartTime =
    startTime && moment(startTime).format(TIME_FORMAT_LONG);
  const prettyEndTime = endTime && moment(endTime).format(TIME_FORMAT_LONG);

  const methods = useForm<SelectedMediaSidePanelFormValues>({
    values: {
      label,
      duration: fastDuration,
      startTime: prettyStartTime,
      endTime: prettyEndTime,
    },
  });

  useEffect(() => {
    const startTimeSubscribtion = methods.watch((values, { name }) => {
      if (name === "startTime") {
        const updatedEndTimeValue = moment(values.startTime, TIME_FORMAT_LONG)
          .add(duration, "milliseconds")
          .format(TIME_FORMAT_LONG);

        const isNewDateValid = moment(
          values.startTime,
          TIME_FORMAT_LONG,
        ).isValid();

        if (isNewDateValid) {
          methods.clearErrors("startTime");
          methods.setValue("endTime", updatedEndTimeValue);
        } else {
          methods.setError("startTime", {
            type: "manual",
            message: t("invalidTime", { ns: "error" }),
          });
        }
      }
    });

    return () => {
      startTimeSubscribtion.unsubscribe();
    };
  }, [duration, methods, t]);

  const deleteScheduleListMediaMutation = useDeleteScheduleListMediaMutation();

  const computeHasNextVariantMediaWithSameParent = (): boolean => {
    if (isOnBaseline) {
      return false;
    }

    const currentVariantTimeSlot = currentTimeslots?.variantTimeslots.find(
      (variant) => variant.id === scheduleId,
    );

    if (!currentVariantTimeSlot) {
      return false;
    }

    const currentUsedSlot = currentVariantTimeSlot?.getUsedSlotById(uuid);

    const nextUsedSlot = currentVariantTimeSlot?.getUsedSlotById(nextMediaId);

    if (!currentUsedSlot && !nextUsedSlot) {
      return false;
    }

    return currentUsedSlot?.parentId === nextUsedSlot?.parentId;
  };

  const hasNextVariantMediaWithSameParent =
    computeHasNextVariantMediaWithSameParent();

  const isDeleteDisabled = Boolean(
    ((moment(startTime).isBefore(moment()) ||
      deleteScheduleListMediaMutation.isLoading ||
      hasMediaVariant) ??
      hasNextVariantMediaWithSameParent) ||
    !isEditMode ||
    isUpdateDisabled,
  );

  const handleClose = (): void =>
    dispatch({ type: "SET_ITEM_SELECTED", id: "" });

  const handleDeleteMedia = (): void => {
    if (uuid) {
      handleClose();
      getCanUpdateSchedule({
        onSuccess: () =>
          deleteScheduleListMediaMutation.mutate({ uuid, scheduleId }),
        onError: () => {
          toast.error(t("programDeleteError"));
        },
      });
    }
  };

  const isDateValid = moment(
    methods.getValues("startTime"),
    TIME_FORMAT_LONG,
  ).isValid();

  const isInPast = moment(startTime).isBefore(moment());

  const isSubmitDisabled =
    ((isInPast || hasMediaVariant) ?? !methods.formState.isDirty) ||
    !isDateValid;

  const getDisabledSubmitMessage = (): string | null => {
    if (isInPast) {
      return t("canNotUpdateMediaInPast");
    }

    if (hasMediaVariant) {
      return t("canNotUpdateMediaWithVariant");
    }

    if (!isDateValid) {
      return t("invalidTime", { ns: "error" });
    }

    if (!isOnBaseline) {
      return t("canNotUpdateVariantMedia");
    }

    return null;
  };

  const getDisabledDeleteMessage = (): string => {
    if (!isEditMode) {
      return t("editModeNeeded");
    }
    if (isUpdateDisabled) {
      return t("canNotDeleteMediaDuringSync");
    }
    if (isInPast) {
      return t("canNotDeleteMediaInPast");
    }

    if (hasMediaVariant) {
      return t("canNotDeleteMediaWithVariant");
    }

    if (hasNextVariantMediaWithSameParent) {
      return t("canNotDeleteVariantMediaWithNextMedia");
    }

    return t("canNotDeleteThisMedia");
  };

  const handleFormSubmit = (data: SelectedMediaSidePanelFormValues): void => {
    const initialStartDay = moment(startTime).format(API_DATE_FORMAT);
    const updatedStartTime = moment(
      `${initialStartDay} ${data.startTime}`,
      "YYYY-MM-DD HH:mm:ss:SSS",
    ).toISOString();

    if (!uuid) return;

    getCanUpdateSchedule({
      onSuccess: () =>
        updateMediaMutation.mutate({
          uuid,
          startTime: updatedStartTime,
        }),
      onError: () => {
        toast.error(t("programUpdateError"));
      },
    });
  };

  const disabledSubmitMessage = getDisabledSubmitMessage();

  const sidePanelActions = [
    {
      label: t("updateSchedule"),
      id: "update-schedule",
      isFullWidth: true,
      isFullHeight: true,
      onClick: methods.handleSubmit(handleFormSubmit),
      isDisabled: isSubmitDisabled,
      tooltipOptions:
        isSubmitDisabled && disabledSubmitMessage
          ? {
              id: "disabled-submit",
              content: disabledSubmitMessage,
            }
          : undefined,
    },
    {
      label: t("deleteMedia"),
      id: "remove-media",
      onClick: (): void => (isDeleteDisabled ? undefined : setShowModal(true)),
      isDisabled: isDeleteDisabled,
      buttonVariant: BUTTON_VARIANTS.DELETE,
      isFullWidth: true,
      isFullHeight: true,
      tooltipOptions: isDeleteDisabled
        ? {
            id: "disabled-delete",
            content: getDisabledDeleteMessage(),
          }
        : undefined,
    },
  ];

  const confirmModalActions = [
    {
      label: t("cancel"),
      onClick: (): void => setShowModal(false),
      buttonVariant: BUTTON_VARIANTS.SECONDARY,
      isFullWidth: true,
    },
    {
      label: t("delete"),
      onClick: handleDeleteMedia,
      buttonVariant: BUTTON_VARIANTS.DELETE,
      isDisabled: isDeleteDisabled,
      isFullWidth: true,
    },
  ];

  const isStartTimeReadOnly = Boolean(
    ((!isEditMode || hasMediaVariant) ?? !isOnBaseline) ||
    isInPast ||
    isUpdateDisabled,
  );

  if (!mediaId) return null;

  const titleBlock = (
    <div className="flex w-full items-center gap-[0.9375rem] overflow-hidden">
      <Icon path={mdiMovieOpenCogOutline} size={0.9375} />
      <div className="flex text-[1.171875rem]">
        <div className="max-w-[325px] truncate">{label}</div>
      </div>
      <SelectedMediaOwnerInfo updatedAt={updatedAt} userId={userId} />
    </div>
  );

  return (
    <SidePanel
      actions={sidePanelActions}
      isOpen={Boolean(timelineItemSelected)}
      onClose={handleClose}
      title={titleBlock}
    >
      <FormProvider
        {...methods} // eslint-disable-line react/jsx-props-no-spreading
      >
        <SelectedMediaSidePanelContent
          endTime={endTime}
          format={format}
          interstitialAdbreaks={interstitialAdbreaks}
          isStartTimeReadOnly={isStartTimeReadOnly}
          picture={poster}
          scheduleId={scheduleId}
          startTime={startTime}
          uuid={uuid}
        />
      </FormProvider>
      <ConfirmModal
        actions={confirmModalActions}
        iconPath={mdiTrashCanOutline}
        isOpen={showModal}
        onClose={(): void => setShowModal(false)}
        size="sm"
        title={t("deleteThisMedia")}
      >
        {t("deleteMediaSchedulingConfirmation")}
      </ConfirmModal>
    </SidePanel>
  );
}

export default SelectedMediaSidePanel;
